import { generatePath, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  AttentionSection,
  AttentionVisit,
  WorkspaceAttention,
} from '@/api/attention/attentionApi.types';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { formatIsoDateTimeToTimestamp, formatIsoDateToDayAndMonth } from '@/utils/dateFormatting';
import { VisitQuickActions } from '@/components/practitioner/shared/VisitQuickActions';
import { AttentionSectionBlock, type AttentionRow } from './AttentionSectionBlock';

const APPOINTMENT_LINK_PREFIX = APPLICATION_ROUTES.workspaceDashboard + '?appointment=';

function hasItems<Item>(section: AttentionSection<Item> | null): section is AttentionSection<Item> {
  return section !== null && section.total > 0;
}

function toVisitRow(visit: AttentionVisit, withMessage: boolean): AttentionRow {
  return {
    key: visit.appointmentId,
    to: APPOINTMENT_LINK_PREFIX + visit.appointmentId,
    primary: visit.patientName + ' · ' + visit.clientName,
    secondary: formatIsoDateToDayAndMonth(visit.date) + ', ' + visit.startTime,
    detail: withMessage ? visit.rescheduleRequestMessage : null,
    actions: null,
  };
}

function toVisitRows(
  section: AttentionSection<AttentionVisit>,
  withMessage: boolean
): AttentionRow[] {
  return section.items.map(function toRow(visit) {
    return toVisitRow(visit, withMessage);
  });
}

interface AttentionSectionsProps {
  attention: WorkspaceAttention;
  isScopedToClient: boolean;
}

export function AttentionSections({ attention, isScopedToClient }: AttentionSectionsProps) {
  const { t } = useTranslation('dashboard');
  const intakeForms = attention.intakeForms;
  const files = attention.filesAwaitingApproval;
  const consents = attention.withdrawnConsents;

  return (
    <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(100%,16rem),1fr))]">
      {hasItems(intakeForms) ? (
        <AttentionSectionBlock
          iconName="ClipboardList"
          title={t('attention.sections.intakeForms')}
          total={intakeForms.total}
          rows={intakeForms.items.map(function toRow(form) {
            return {
              key: form.submissionId,
              to: generatePath(APPLICATION_ROUTES.workspaceIntakeSubmission, {
                submissionId: form.submissionId,
              }),
              primary: form.petName === null ? t('attention.unnamedPet') : form.petName,
              secondary:
                form.submittedAt === null
                  ? form.clientName
                  : form.clientName + ' · ' + formatIsoDateTimeToTimestamp(form.submittedAt),
              detail: null,
              actions: null,
            };
          })}
          footer={
            isScopedToClient ? null : (
              <Link
                to={APPLICATION_ROUTES.workspaceIntake}
                className="text-xs font-semibold text-zooprimary hover:underline mt-2"
              >
                {t('attention.viewAllForms')}
              </Link>
            )
          }
        />
      ) : null}
      {hasItems(attention.rescheduleRequests) ? (
        <AttentionSectionBlock
          iconName="RefreshCw"
          title={t('attention.sections.rescheduleRequests')}
          total={attention.rescheduleRequests.total}
          rows={toVisitRows(attention.rescheduleRequests, true)}
          footer={null}
        />
      ) : null}
      {hasItems(attention.visitsAwaitingOutcome) ? (
        <AttentionSectionBlock
          iconName="Clock"
          title={t('attention.sections.visitsAwaitingOutcome')}
          total={attention.visitsAwaitingOutcome.total}
          rows={attention.visitsAwaitingOutcome.items.map(function toRow(visit) {
            return {
              ...toVisitRow(visit, false),
              actions: (
                <VisitQuickActions
                  appointmentId={visit.appointmentId}
                  patientId={visit.patientId}
                  actions={['writeNote', 'noShow']}
                />
              ),
            };
          })}
          footer={null}
        />
      ) : null}
      {hasItems(attention.clientCancellations) ? (
        <AttentionSectionBlock
          iconName="X"
          title={t('attention.sections.clientCancellations')}
          total={attention.clientCancellations.total}
          rows={toVisitRows(attention.clientCancellations, false)}
          footer={null}
        />
      ) : null}
      {hasItems(files) ? (
        <AttentionSectionBlock
          iconName="FileText"
          title={t('attention.sections.filesAwaitingApproval')}
          total={files.total}
          rows={files.items.map(function toRow(file) {
            return {
              key: file.fileId,
              to: generatePath(APPLICATION_ROUTES.workspacePatientDetail, {
                patientId: file.patientId,
              }),
              primary: file.filename,
              secondary: file.patientName + ' · ' + formatIsoDateTimeToTimestamp(file.uploadedAt),
              detail: null,
              actions: null,
            };
          })}
          footer={null}
        />
      ) : null}
      {hasItems(consents) ? (
        <AttentionSectionBlock
          iconName="ShieldCheck"
          title={t('attention.sections.withdrawnConsents')}
          total={consents.total}
          rows={consents.items.map(function toRow(consent) {
            return {
              key: consent.clientId + consent.consentTitle,
              to: generatePath(APPLICATION_ROUTES.workspaceClientDetail, {
                clientId: consent.clientId,
              }),
              primary: consent.clientName,
              secondary:
                consent.consentTitle + ' · ' + formatIsoDateTimeToTimestamp(consent.withdrawnAt),
              detail: null,
              actions: null,
            };
          })}
          footer={null}
        />
      ) : null}
      {hasItems(attention.petChangeRequests) ? (
        <AttentionSectionBlock
          iconName="Edit"
          title={t('attention.sections.petChangeRequests')}
          total={attention.petChangeRequests.total}
          rows={attention.petChangeRequests.items.map(function toRow(request) {
            return {
              key: request.requestId,
              to: generatePath(APPLICATION_ROUTES.workspacePatientDetail, {
                patientId: request.patientId,
              }),
              primary: request.patientName,
              secondary:
                request.clientName + ' · ' + formatIsoDateTimeToTimestamp(request.requestedAt),
              detail: null,
              actions: null,
            };
          })}
          footer={null}
        />
      ) : null}
    </div>
  );
}
